/**
 * assets/cross-sell-cart.js — v3 (2026-08-20)
 *
 * Keeps the Rebuy cart-drawer upsell alive across the theme's cart re-renders,
 * AND makes its "Add" button actually add. v2 fixed only the first of the three
 * faults behind "the upsell sometimes does nothing"; the other two were living
 * in the Intelligems VARIATION script (window.PPCartRedesign) and therefore
 * never reached shoppers on the CONTROL bucket. This file ports them into the
 * theme so control and variation behave the same.
 *
 * THE THREE FAULTS (all traced on the live site)
 * ----------------------------------------------
 * theme.js redraws the drawer by assigning `.cart-drawer__inner.innerHTML`
 * (CartDrawer._onCartChanged — and note it does so 1250ms AFTER a cart change,
 * i.e. squarely inside the window where a shopper is clicking the upsell), or
 * by `replaceChildren()` on a `cart:refresh`. Rebuy has a live Vue component
 * mounted inside that HTML, so either one orphans Vue's element:
 *
 *     theme.js  -> set innerHTML on DIV.cart-drawer__inner
 *     rebuy     -> Vue.patch -> removeVnodes -> removeNode -> removeChild
 *                  (the [data-rebuy-id] mount itself)
 *
 *   (a) THE ADD SUCCEEDS BUT NOTHING REDRAWS. `/cart/add.js` returns 200 with
 *       the line in it (`properties._source: "Rebuy"`), but the drawer never
 *       repaints, so the shopper sees no change and concludes the button is
 *       broken; navigate away and back and the item is there, because the
 *       drawer is server-rendered. theme.js already owns the redraw —
 *
 *           document 'rebuy:cart.add' -> CartDrawer._onRebuyCartAdd()
 *                                     -> document 'cart:refresh'
 *                                     -> fetch ?section_id=… + replaceChildren()
 *
 *       — but Rebuy never dispatches `rebuy:cart.add` on this site (its Vue
 *       instance is orphaned, so its emitter is gone; verified by listening for
 *       rebuy:cart.add / rebuy:cart.change / cart:change / cart:refresh across
 *       a real click — not one fires). So we watch the wire and dispatch
 *       `cart:refresh` ourselves when a Rebuy add goes past.  [fixed in v2]
 *
 *   (b) THE CLICK IS SWALLOWED. A widget that swaps its nodes between mousedown
 *       and mouseup eats the gesture: the browser fires `click` on the nearest
 *       common ancestor of the two targets, never on the button, so Rebuy's own
 *       handler never runs. Nothing is added at all. This is the intermittent
 *       "Add does nothing" — it depends on whether a re-render lands inside the
 *       ~120ms a human holds the button.  [NEW in v3: click repair]
 *
 *   (c) THE WIDGET IS DEAD. Sometimes a real `click` reaches the button and
 *       ZERO requests go out: the markup is on screen but the Vue instance
 *       behind it was orphaned by a re-render, so there is no live handler. It
 *       is a race, which is why the same page is fine one minute and dead the
 *       next ("it works after a few minutes of activity").  [NEW in v3: rescue]
 *
 * v3 ALSO CALMS THE RE-INIT LOOP, because that loop is what CAUSES (b).
 * v2 called `Rebuy.init()` on EVERY 250ms tick for up to 6s, and any cart write
 * restarted it — i.e. the widget re-rendered under the cursor several times a
 * second. v3 re-inits at most once a second, and never while the shopper is
 * mid-gesture on the widget (a pointerdown inside `[data-rebuy-id]` opens a
 * quiet window; pending refreshes are deferred, not dropped).
 *
 * INTEROP WITH THE INTELLIGEMS TEST (PP #22 cart drawer redesign)
 * --------------------------------------------------------------
 * The variation script carries its own copy of the click repair and the rescue
 * add. Two copies would add the line twice, so every ACTION in this file (the
 * `cart:refresh` dispatch, the re-issued click, the rescue add) stands down
 * when `window.PPCartRedesign` is present. The check is made at action time,
 * not at boot, because the A/B tool may inject after this file has run. The
 * widget keep-alive below stays active in both buckets — it is idempotent and
 * the variation relies on the mount existing.
 *
 * EMPTY-CART WIDGET (2026-07-27)
 * ------------------------------
 *   sections/cart-drawer.liquid renders its OWN mount in the empty-cart branch,
 *   from a separate setting (`rebuy_empty_cart_widget`):
 *
 *       <div class="cart-drawer__empty-recommendations"
 *            data-rebuy-id="{{ section.settings.rebuy_empty_cart_widget }}"></div>
 *
 *   so the first add off an empty cart is an ordinary re-render, not a cold
 *   start. Because the two branches can carry DIFFERENT widget ids, this file
 *   does not hard-code 261177: the ids are published as `data-rebuy-cart-widget`
 *   and `data-rebuy-empty-widget` on <cart-drawer> and read from there.
 *
 * Debug: window.CrossSellCart.repairs() / .rescues() / .refresh()
 */
(function () {
  'use strict';

  var REBUY_WIDGET_ID = '261177';           // fallback only — the real ids come off <cart-drawer>
  var CART_DRAWER_SELECTOR = '#cart-drawer, cart-drawer';
  var MOUNT_HOST_SELECTOR = '.cart-drawer__inner > .v-stack';
  var EMPTY_MOUNT_SELECTOR = '.cart-drawer__empty-recommendations';
  var TICK_MS = 250;
  var MAX_TICKS = 24;          // 6s of retries
  var INIT_EVERY_TICKS = 4;    // …but re-init at most once a second (see (b) above)
  var DEBOUNCE_MS = 200;
  var COOLDOWN_MS = 1500;      // ignore repeat triggers inside this window
  var REFRESH_DEDUPE_MS = 500; // one add, one redraw
  var GESTURE_QUIET_MS = 1200; // no re-init while the shopper is on the widget
  var CLICK_REPAIR_MS = 220;   // re-issue a swallowed click after this long
  var ADD_WAIT_MS = 1200;      // …and if still no request, add it ourselves

  var REBUY_BODY = /"_source"\s*:\s*"Rebuy"|"_attribution"\s*:\s*"Rebuy/;
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var ADD_URL = /\/cart\/add(\.js)?(\?|$)/;

  var refreshTimer = null;
  var pollTimer = null;        // the ONE interval, so it can always be cleared
  var lastRun = 0;
  var lastRefresh = 0;
  var quietUntil = 0;
  var drawerObserver = null;
  var eventsBound = false;

  var pendingClick = null;
  var repairs = 0;             // how many clicks had to be re-issued
  var rescues = 0;             // …and how many adds had to be made outright
  var lastAddSeen = 0;

  /* The Intelligems variation ships its own copy of the repair + rescue below.
     Running both would add the line twice, so defer to it whenever it is on the
     page. Evaluated at ACTION time — the A/B tool can inject after this file. */
  function variationOwnsRepairs() {
    return !!window.PPCartRedesign;
  }

  function now() {
    return new Date().getTime();
  }

  function getDrawer() {
    return document.querySelector(CART_DRAWER_SELECTOR);
  }

  /* The cart-with-items widget and the empty-cart widget are two independent
     theme settings (sections/cart-drawer.liquid publishes both ids as attributes
     on <cart-drawer>). Only one branch is ever rendered, so only one mount can
     exist at a time — but which id it carries depends on that branch. */
  function getFilledWidgetId() {
    var drawer = getDrawer();
    return (drawer && drawer.getAttribute('data-rebuy-cart-widget')) || REBUY_WIDGET_ID;
  }

  function getEmptyWidgetId() {
    var drawer = getDrawer();
    return (drawer && drawer.getAttribute('data-rebuy-empty-widget')) || '';
  }

  function getMount() {
    var drawer = getDrawer();
    if (!drawer) return null;

    // whichever branch is on screen — the empty-state mount is rendered by Liquid
    // and carries .cart-drawer__empty-recommendations
    var emptyId = getEmptyWidgetId();

    return drawer.querySelector('[data-rebuy-id="' + getFilledWidgetId() + '"]')
        || (emptyId ? drawer.querySelector('[data-rebuy-id="' + emptyId + '"]') : null)
        || drawer.querySelector(EMPTY_MOUNT_SELECTOR + '[data-rebuy-id]');
  }

  /* Rebuy's own Vue teardown can remove the mount after a re-render, so put one
     back rather than giving up. Only ever recreated for the cart-WITH-ITEMS
     branch: the empty state's mount is server-rendered, and fabricating one in a
     drawer whose Liquid didn't ask for it would show the widget where the
     merchant turned it off. */
  function ensureMount() {
    var existing = getMount();
    if (existing) return existing;

    var drawer = getDrawer();
    if (!drawer) return null;

    var host = drawer.querySelector(MOUNT_HOST_SELECTOR)
            || drawer.querySelector('.cart-drawer__inner');
    if (!host) return null;                       // empty state, nothing to hang it on

    var mount = document.createElement('div');
    mount.setAttribute('data-rebuy-id', getFilledWidgetId());
    host.appendChild(mount);
    return mount;
  }

  function isDrawerOpen() {
    var drawer = getDrawer();
    if (!drawer) return false;

    return (
      drawer.hasAttribute('open') ||
      drawer.classList.contains('is-open') ||
      drawer.classList.contains('active') ||
      drawer.getAttribute('aria-hidden') === 'false' ||
      drawer.style.visibility === 'visible'
    );
  }

  function hasRebuyProducts(mount) {
    return !!(mount && mount.querySelector('.rebuy-product-block'));
  }

  function runRebuyInit() {
    if (!window.Rebuy) return false;

    if (typeof window.Rebuy.init === 'function') {
      window.Rebuy.init();
      return true;
    }

    if (window.Rebuy.WidgetManager && typeof window.Rebuy.WidgetManager.init === 'function') {
      window.Rebuy.WidgetManager.init();
      return true;
    }

    if (window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.init === 'function') {
      window.Rebuy.SmartCart.init();
      return true;
    }

    return false;
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  /* ------------------------------------------------------- widget keep-alive */

  function refreshRebuyWidget(force) {
    var stamp = now();

    // already showing products and nobody is claiming the cart changed —
    // leave it alone. Re-initialising a healthy widget is what swallows clicks.
    if (!force && hasRebuyProducts(getMount())) return;

    // a burst of mutations is one refresh, not twenty
    if (!force && stamp - lastRun < COOLDOWN_MS) return;

    // the shopper's finger is on the widget: re-rendering it right now is
    // exactly fault (b). Defer — never drop, the widget may still be empty.
    if (stamp < quietUntil) {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(function () {
        refreshRebuyWidget(force);
      }, quietUntil - stamp + 50);
      return;
    }

    clearTimeout(refreshTimer);

    refreshTimer = setTimeout(function () {
      lastRun = now();
      stopPolling();                       // <- the fix for the runaway loop

      var mount = ensureMount();

      if (force || !hasRebuyProducts(mount)) {
        if (mount) {
          mount.removeAttribute('data-initialized');

          var brokenWidget = mount.querySelector('[id^="rebuy-widget-"]');

          if (brokenWidget && !hasRebuyProducts(brokenWidget)) {
            brokenWidget.remove();
          }
        }
      }

      var ticks = 0;

      pollTimer = setInterval(function () {
        ticks += 1;

        // A missing mount is the middle of the theme's innerHTML swap — the
        // exact moment this rescue exists for. Rebuild it and keep going;
        // never clearInterval here, that is what used to strand the widget.
        var currentMount = ensureMount();

        // v2 called init() on every single tick (4x a second, for 6s, restarted
        // by every cart write) and that is what re-rendered the widget under the
        // cursor. Once a second is plenty, and never mid-gesture.
        if (currentMount && now() >= quietUntil && (ticks === 1 || ticks % INIT_EVERY_TICKS === 0)) {
          runRebuyInit();
        }

        if (hasRebuyProducts(currentMount) || ticks >= MAX_TICKS) {
          stopPolling();

          setTimeout(function () {
            window.dispatchEvent(new Event('resize'));

            if (window.jQuery) {
              window.jQuery(window).trigger('resize');
            }
          }, 150);
        }
      }, TICK_MS);
    }, DEBOUNCE_MS);
  }

  function bindEvents() {
    if (eventsBound) return;
    eventsBound = true;

    [
      'cart:updated',
      'cart:refresh',
      'cart:change',
      'theme:cart:change',
      'ajaxProduct:added',
      'product:added',
      'variant:add',
      'rebuy:cart.change',
      'rebuy:smartcart.ready'
    ].forEach(function (eventName) {
      document.addEventListener(eventName, function () {
        refreshRebuyWidget(true);
      });

      window.addEventListener(eventName, function () {
        refreshRebuyWidget(true);
      });
    });
  }

  /* ------------------------------------------------- (a) make the drawer redraw */

  /**
   * The cross-sell Add works — /cart/add.js returns 200 and the line really is
   * in the cart. What never happens is the drawer redrawing, so the shopper
   * sees no change and concludes the button is broken.
   *
   * theme.js already has the wiring for this:
   *
   *     document 'rebuy:cart.add' -> CartDrawer._onRebuyCartAdd()
   *                               -> document 'cart:refresh'
   *                               -> _onCartRefresh(): fetch ?section_id=...
   *                                  and replaceChildren()
   *
   * but Rebuy never dispatches `rebuy:cart.add` here. So dispatch cart:refresh
   * ourselves when we see a Rebuy add go past. The response body carries
   * `properties._source == "Rebuy"`, which keeps this to Rebuy's own adds and
   * leaves the theme's normal add path (which refreshes itself) alone.
   */
  function refreshCartDrawer() {
    if (variationOwnsRepairs()) return;      // the variation dispatches its own

    var stamp = now();
    if (stamp - lastRefresh < REFRESH_DEDUPE_MS) return;
    lastRefresh = stamp;

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    // the redraw replaces the drawer's children wholesale and Rebuy's Vue tears
    // its own mount out when that happens — put one back so the carousel does
    // not vanish behind the line that was just added
    setTimeout(function () { refreshRebuyWidget(true); }, 900);
    setTimeout(function () { refreshRebuyWidget(true); }, 2200);
  }

  /* --------------------------------------------- (b) repair a swallowed click */

  /**
   * Remember which CARD the pointer went down on, and if no click ever reaches
   * a `.rebuy-button`, re-issue it on that card's CURRENT button ~220ms later.
   * If the real click did land, `landed` is set and this does nothing — so it
   * can never add twice.
   */
  function cardKey(card) {
    if (!card) return null;

    var m = String(card.className || '').match(/product-id-(\d+)/);
    if (m) return 'id:' + m[1];

    var title = card.querySelector('.rebuy-product-title');
    return title ? 'title:' + title.textContent.replace(/\s+/g, ' ').trim() : null;
  }

  function buttonForKey(key) {
    if (!key) return null;

    var cards = document.querySelectorAll('#cart-drawer .rebuy-product-block');

    for (var i = 0; i < cards.length; i++) {
      if (cardKey(cards[i]) === key) return cards[i].querySelector('.rebuy-button');
    }

    return null;
  }

  function closestIn(node, selector) {
    return (node && node.closest) ? node.closest(selector) : null;
  }

  /** `.rebuy-button` is Rebuy's generic button class — only stand in for the one
   *  that adds to cart, and never for a disabled (sold-out) one. */
  function isAddButton(btn) {
    if (!btn || btn.disabled) return false;

    var label = btn.getAttribute('aria-label') || btn.textContent || '';
    return /\badd\b/i.test(label);
  }

  function installClickRepair() {
    document.addEventListener('pointerdown', function (event) {
      // any press inside the widget opens the quiet window, so the keep-alive
      // above cannot re-render the thing the shopper is pressing
      if (closestIn(event.target, '[data-rebuy-id]')) {
        quietUntil = now() + GESTURE_QUIET_MS;
      }

      var btn = closestIn(event.target, '.rebuy-button');
      if (!btn || !closestIn(btn, '#cart-drawer')) return;

      pendingClick = { key: cardKey(closestIn(btn, '.rebuy-product-block')), landed: false };
    }, true);

    document.addEventListener('click', function (event) {
      var btn = closestIn(event.target, '.rebuy-button');
      if (!btn || !closestIn(btn, '#cart-drawer')) return;

      quietUntil = now() + GESTURE_QUIET_MS;

      if (pendingClick) pendingClick.landed = true;
      if (isAddButton(btn)) watchAdd(closestIn(btn, '.rebuy-product-block'));
    }, true);

    document.addEventListener('pointerup', function () {
      var pending = pendingClick;
      if (!pending || pending.landed) return;

      setTimeout(function () {
        if (pending.landed || pendingClick !== pending) return;
        if (variationOwnsRepairs()) return;

        var btn = buttonForKey(pending.key);
        if (!btn) return;

        pending.landed = true;               // whatever happens, only once
        repairs++;

        try {
          btn.click();
        } catch (e) { /* the widget went away — let it be */ }
      }, CLICK_REPAIR_MS);
    }, true);
  }

  /* ------------------------------------------------ (c) rescue a dead widget */

  /**
   * Even with the gesture repaired, the click sometimes reaches Rebuy's button
   * and NOTHING goes out on the wire — the markup is on screen with no live
   * handler behind it. So if a click on an Add button produces no /cart/add
   * within 1.2s, add the line ourselves, with the properties Rebuy itself sends
   * (captured from a working add) so the attribution is identical and the
   * response is recognised by the hook above. The variant comes off the card's
   * own product link (`?variant=…`); a card that does not name a variant is a
   * picker, not a one-click add, so it is left alone.
   */
  function noteAddRequest() {
    lastAddSeen = now();
  }

  function variantFromCard(card) {
    var link = card && card.querySelector('a[href*="/products/"]');
    var href = link && link.getAttribute('href');
    var match = href && href.match(/[?&]variant=(\d+)/);
    return match ? match[1] : null;
  }

  /* A Rebuy variant/quick-view modal means the click DID reach a live handler
     and the shopper still has to choose something — never add behind it. */
  function rebuyModalOpen() {
    var modal = document.querySelector('.rebuy-modal, .rebuy-widget-modal, [class*="rebuy"][class*="modal"]');
    return !!(modal && modal.offsetParent !== null);
  }

  function watchAdd(card) {
    var variant = variantFromCard(card);
    if (!variant) return;

    var at = now();

    setTimeout(function () {
      if (lastAddSeen >= at) return;         // Rebuy handled it
      if (variationOwnsRepairs()) return;    // the variation will rescue it
      if (rebuyModalOpen()) return;

      rescueAdd(variant);
    }, ADD_WAIT_MS);
  }

  function rescueAdd(variant) {
    rescues++;
    noteAddRequest();                        // so a second watcher stands down

    try {
      window.fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variant,
            quantity: 1,
            properties: {
              _source: 'Rebuy',
              _widget_id: getFilledWidgetId(),
              _attribution: 'Rebuy Cart'
            }
          }]
        })
      }).then(function (response) {
        // the response carries _source:"Rebuy", so the fetch hook below redraws
        // the drawer on its own — nothing more to do here
        if (!response || !response.ok) return;
      }).catch(function () {});
    } catch (e) { /* no fetch: leave the cart alone */ }
  }

  /* ------------------------------------------------------------ wire hooks */

  function bodyIsRebuyAdd(response) {
    // never consume the caller's body — always work on a clone
    try {
      return response.clone().text().then(function (body) {
        return REBUY_BODY.test(body);
      }).catch(function () { return false; });
    } catch (e) {
      return Promise.resolve(false);
    }
  }

  function hookFetch() {
    if (!window.fetch || window.__cwRebuyDrawerFetchHooked) return;

    var originalFetch = window.fetch;

    window.fetch = function () {
      var args = arguments;
      var input = args[0];
      var requestUrl = '';

      try {
        requestUrl = typeof input === 'string' ? input : (input && input.url) ? input.url : '';
        if (ADD_URL.test(requestUrl)) noteAddRequest();
      } catch (e) { /* never let this touch the caller's request */ }

      return originalFetch.apply(this, args).then(function (response) {
        try {
          if (ADD_URL.test(requestUrl) && response.ok) {
            bodyIsRebuyAdd(response).then(function (fromRebuy) {
              if (fromRebuy) refreshCartDrawer();
            });
          }

          if (CART_WRITE.test(requestUrl)) {
            setTimeout(function () {
              refreshRebuyWidget(true);
            }, 300);
          }
        } catch (e) {}

        return response;
      });
    };

    window.__cwRebuyDrawerFetchHooked = true;
  }

  function hookXhr() {
    if (!window.XMLHttpRequest || window.__cwRebuyDrawerXhrHooked) return;

    var originalOpen = XMLHttpRequest.prototype.open;

    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        this.__cwRebuyCartRequest = typeof url === 'string' && CART_WRITE.test(url);
        if (typeof url === 'string' && ADD_URL.test(url)) noteAddRequest();
      } catch (e) {}

      return originalOpen.apply(this, arguments);
    };

    var originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.send = function () {
      if (this.__cwRebuyCartRequest) {
        this.addEventListener('loadend', function () {
          var body = '';
          try { body = this.responseText || ''; } catch (e) { body = ''; }

          if (REBUY_BODY.test(body)) refreshCartDrawer();

          setTimeout(function () {
            refreshRebuyWidget(true);
          }, 300);
        });
      }

      return originalSend.apply(this, arguments);
    };

    window.__cwRebuyDrawerXhrHooked = true;
  }

  function observeDrawer() {
    var drawer = getDrawer();

    if (!drawer || drawerObserver) return;

    drawerObserver = new MutationObserver(function (mutations) {
      if (!isDrawerOpen()) return;

      // Ignore anything that happened INSIDE the Rebuy widget: that is Rebuy
      // rendering, and treating it as "the drawer changed" is what turned this
      // observer into a re-init loop that fed itself.
      var relevant = mutations.some(function (m) {
        var t = m.target;
        return !(t && t.closest && t.closest('[data-rebuy-id]'));
      });

      if (relevant) refreshRebuyWidget(false);
    });

    drawerObserver.observe(drawer, {
      childList: true,
      subtree: true
      // no attributeFilter: attribute churn (class/style toggles from the theme,
      // from Flickity, or from any A/B script) is not a reason to re-init.
    });
  }

  function init() {
    bindEvents();
    hookFetch();
    hookXhr();
    installClickRepair();
    observeDrawer();
    refreshRebuyWidget(false);
  }

  window.CrossSellCart = {
    version: 3,
    refresh: function () { refreshRebuyWidget(true); },
    repairs: function () { return repairs; },
    rescues: function () { return rescues; }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
